import {forwardRef, ReactNode, Ref, useId} from 'react';
import './flat-input.scss';
import {
  FormControl,
  FilledTextFieldProps as MUITextFieldProps,
  Slide,
  TextField,
  SxProps,
  Stack,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import {Theme} from '@mui/material/styles';
import {IoMdArrowDropdown, IoMdArrowDropup} from 'react-icons/io';
import {Pallete} from '../../theme/bluebird';
import {Tooltip} from '../tooltip/tooltip';

export interface FlatInputProps extends Omit<MUITextFieldProps, 'variant'> {
  label?: string;
  errors?: string;
  readonly?: boolean;
  counter?: {current: number; max: number};
  onIncrement?: () => void;
  onDecrement?: () => void;
  sx?: SxProps<Theme>;
  disabledTooltip?: string | ReactNode;
  insideLabel?: boolean;
}

export const FlatInput = forwardRef(
  (
    {
      label,
      errors,
      required,
      counter,
      disabled,
      readonly,
      sx,
      onIncrement,
      onDecrement,
      color = 'gray',
      disabledTooltip,
      insideLabel,
      ...rest
    }: FlatInputProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const hasError = !!errors;

    const id = useId();

    const uniqueId = rest.id ?? `input_${id}`;

    const readonlyStyles: SxProps<Theme> = {};
    if (readonly === true) {
      // @ts-ignore
      readonlyStyles['& .MuiInput-underline'] = {
        ':hover:before': {
          border: 'none !important',
        },
      };
    }

    return (
      <FormControl
        className={`flat-input ${disabled ? 'disabled' : ''}`}
        sx={{width: '100%', ...sx}}
      >
        {label && !insideLabel && (
          <Box className="label-container">
            <Typography component="label" htmlFor={uniqueId} noWrap>
              {label}
            </Typography>
            {required && !disabled && <Typography component="span">*</Typography>}
          </Box>
        )}

        <Tooltip.NotAllowedIf condition={disabled} title={disabledTooltip}>
          <Stack flexDirection="row" sx={{width: '100%'}}>
            <TextField
              id={uniqueId}
              ref={ref}
              disabled={disabled}
              label={insideLabel && label}
              {...rest}
              sx={{
                width: '100%',
                '.MuiFilledInput-root': {
                  borderRadius: rest.multiline ? '5px!important' : '5px 5px 0px 0px',
                },
                '.MuiInputBase-root': {
                  padding: '8.5px 12px',
                },
                ...readonlyStyles,
              }}
              variant="filled"
              inputProps={{
                ...rest.inputProps,
                readOnly: readonly,
              }}
              color={color}
              error={hasError}
              InputProps={{
                ...rest.InputProps,
                readOnly: disabled || rest.InputProps?.readOnly,
                sx: {
                  pointerEvents: disabled || rest.InputProps?.readOnly ? 'none' : 'auto',
                  gap: 1,
                },
              }}
            />
            {rest.type === 'number' && (
              <Stack
                sx={{
                  height: '100%',
                  justifyContent: 'center',
                }}
              >
                {onIncrement && (
                  <IconButton
                    size="small"
                    style={{width: '20px', height: '20px', padding: '0'}}
                    onClick={onIncrement}
                  >
                    <IoMdArrowDropup fontSize="16px" />
                  </IconButton>
                )}
                {onDecrement && (
                  <IconButton
                    size="small"
                    style={{
                      width: '20px',
                      height: '20px',
                      padding: '0',
                    }}
                    onClick={onDecrement}
                  >
                    <IoMdArrowDropdown fontSize="16px" />
                  </IconButton>
                )}
              </Stack>
            )}
          </Stack>
        </Tooltip.NotAllowedIf>

        {hasError && (
          <div className="error-box">
            <Slide direction="left" in={hasError} mountOnEnter unmountOnExit>
              <span className="input-error" style={{color: Pallete.red.base}}>
                {errors}
              </span>
            </Slide>
            {!disabled && counter && counter.current !== 0 && (
              <span
                className="max-counter"
                style={{
                  color: counter.current <= counter.max ? Pallete.gray.light : Pallete.red.base,
                }}
              >
                {counter.current}/{counter.max}
              </span>
            )}
          </div>
        )}
      </FormControl>
    );
  }
);

export default FlatInput;
